import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IoSearchOutline } from 'react-icons/io5';
import {
  sellersApi,
  type SellerAccountBlockDto,
  type SellerProfileDeletionDto,
} from '@modules/sellers/api/sellers.api';
import {
  SellerAdminStatusBadge,
  SellerProfileChangesTag,
} from '@modules/sellers/components/SellerAdminStatusBadge';
import {
  sellerApplicationsModerationApi,
  type SellerApplicationRow,
  type SellerApplicationStatus,
} from '@modules/seller-applications/api/seller-applications-moderation.api';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import { Input, QuietSelect } from '@shared/ui';
import { formatPhoneNumber } from '@shared/utils';
import { useDebounce } from '@shared/hooks';
import '@shared/styles/page.css';
import './SellersPage.css';

type UnifiedSellerRow =
  | {
      kind: 'seller';
      id: number;
      shopName: string;
      slug: string;
      status: SellerApplicationStatus | null;
      inn: string | null;
      accountType: string | null;
      phone: string | null;
      products: number;
      pickupPoints: number;
      sellerId: number;
      applicationId: number | null;
      accountBlock: SellerAccountBlockDto | null;
      profileHidden: boolean;
      profileDeletion: SellerProfileDeletionDto | null;
    }
  | {
      kind: 'application';
      id: number;
      shopName: string | null;
      slug: string | null;
      status: SellerApplicationStatus;
      inn: string | null;
      accountType: string | null;
      phone: string | null;
      products: null;
      pickupPoints: null;
      sellerId: null;
      applicationId: number;
    };

type SellerStatusFilter =
  | 'all'
  | 'pending'
  | 'profile-changes'
  | 'approved'
  | 'rejected'
  | 'blocked'
  | 'deletion'
  | 'hidden';

const SELLER_STATUS_OPTIONS: { id: SellerStatusFilter; label: string }[] = [
  { id: 'all', label: 'Все' },
  { id: 'pending', label: 'На модерации' },
  { id: 'profile-changes', label: 'Изменения витрины' },
  { id: 'approved', label: 'Одобренные' },
  { id: 'rejected', label: 'Отклонённые' },
  { id: 'blocked', label: 'Заблокированные' },
  { id: 'deletion', label: 'Заявки на удаление' },
  { id: 'hidden', label: 'Скрытые' },
];

function matchesSellerOperationalFilter(
  row: UnifiedSellerRow,
  filter: 'blocked' | 'deletion' | 'hidden',
): boolean {
  if (row.kind !== 'seller') return false;
  if (filter === 'blocked') return row.accountBlock != null;
  if (row.accountBlock != null) return false;
  const deletionPending = row.profileDeletion != null && !row.profileDeletion.deletedAt;
  if (filter === 'deletion') return deletionPending;
  if (deletionPending || row.profileDeletion?.deletedAt) return false;
  return row.profileHidden;
}

function accountTypeLabel(accountType: string | null | undefined): string {
  if (accountType === 'LEGAL_ENTITY') return 'ООО';
  if (accountType === 'INDIVIDUAL_ENTREPRENEUR') return 'ИП';
  if (accountType === 'SELF_EMPLOYED') return 'Самозанятый';
  return '—';
}

type SellersTableRow =
  | (Omit<Extract<UnifiedSellerRow, { kind: 'seller' }>, 'id'> & {
      id: string;
    })
  | (Omit<Extract<UnifiedSellerRow, { kind: 'application' }>, 'id'> & {
      id: string;
    });

export default function SellersPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SellerStatusFilter | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 600);
  const { data: sellers = [], isLoading: isSellersLoading } = useQuery({
    queryKey: ['sellers', debouncedSearchQuery],
    queryFn: () => sellersApi.getAll(debouncedSearchQuery),
  });
  const { data: pendingApplications = [], isLoading: isApplicationsLoading, isSuccess: pendingLoaded } =
    useQuery({
      queryKey: ['seller-applications-moderation', 'PENDING_MODERATION', debouncedSearchQuery],
      queryFn: () =>
        sellerApplicationsModerationApi.list('PENDING_MODERATION', debouncedSearchQuery),
      enabled: statusFilter == null || statusFilter === 'all' || statusFilter === 'pending',
    });
  const { data: rejectedApplications = [], isLoading: isRejectedLoading } = useQuery({
    queryKey: ['seller-applications-moderation', 'REJECTED', debouncedSearchQuery],
    queryFn: () => sellerApplicationsModerationApi.list('REJECTED', debouncedSearchQuery),
    enabled: statusFilter === 'rejected',
  });
  const { data: profileChanges = [], isSuccess: profileChangesLoaded } = useQuery({
    queryKey: ['seller-profile-changes-moderation'],
    queryFn: () => sellerApplicationsModerationApi.listProfileChanges(),
  });
  const sellerIdsWithProfileChanges = useMemo(
    () =>
      new Set(
        profileChanges
          .map((row) => row.sellerId)
          .filter((id): id is number => id != null),
      ),
    [profileChanges],
  );

  useEffect(() => {
    if (statusFilter != null || !pendingLoaded || !profileChangesLoaded) return;
    if (debouncedSearchQuery.trim()) {
      setStatusFilter('all');
      return;
    }
    setStatusFilter(
      pendingApplications.length > 0
        ? 'pending'
        : sellerIdsWithProfileChanges.size > 0
          ? 'profile-changes'
          : 'all',
    );
  }, [
    statusFilter,
    pendingLoaded,
    profileChangesLoaded,
    pendingApplications.length,
    sellerIdsWithProfileChanges.size,
    debouncedSearchQuery,
  ]);

  const applicationRowsWithoutSeller: UnifiedSellerRow[] = pendingApplications
    .filter((row: SellerApplicationRow) => row.sellerId == null)
    .map((row: SellerApplicationRow) => ({
      kind: 'application',
      id: row.id,
      shopName: row.seller?.shopName ?? row.companyName ?? null,
      slug: row.seller?.slug ?? null,
      status: row.status,
      inn: row.inn,
      accountType: row.accountType,
      phone: row.user?.phone ?? null,
      products: null,
      pickupPoints: null,
      sellerId: null,
      applicationId: row.id,
    }));

  const sellerRows: UnifiedSellerRow[] = sellers.map((row) => ({
    kind: 'seller',
    id: row.id,
    shopName: row.shopName,
    slug: row.slug,
    status: (row.application?.status as SellerApplicationStatus | null | undefined) ?? null,
    inn:
      row.application?.inn != null ? String(row.application.inn as unknown) : null,
    accountType:
      typeof row.application?.accountType === 'string' ? row.application.accountType : null,
    phone: row.application?.user?.phone ?? null,
    products: row._count.products,
    pickupPoints: row._count.pickupPoints,
    sellerId: row.id,
    applicationId: row.application?.id ?? null,
    accountBlock: row.accountBlock ?? null,
    profileHidden: Boolean(row.profileHidden),
    profileDeletion: row.profileDeletion ?? null,
  }));

  const rejectedRows: UnifiedSellerRow[] = rejectedApplications.map((row: SellerApplicationRow) => ({
    kind: 'application',
    id: row.id,
    shopName: row.seller?.shopName ?? row.companyName ?? null,
    slug: row.seller?.slug ?? null,
    status: row.status,
    inn: row.inn,
    accountType: row.accountType,
    phone: row.user?.phone ?? null,
    products: null,
    pickupPoints: null,
    sellerId: null,
    applicationId: row.id,
  }));

  const mergedList: UnifiedSellerRow[] = [...applicationRowsWithoutSeller, ...sellerRows];
  const list: UnifiedSellerRow[] =
    statusFilter === 'pending'
      ? mergedList.filter((row) => row.status === 'PENDING_MODERATION')
      : statusFilter === 'profile-changes'
        ? sellerRows.filter((row) => row.sellerId != null && sellerIdsWithProfileChanges.has(row.sellerId))
        : statusFilter === 'approved'
          ? sellerRows.filter((row) => row.status === 'APPROVED' || row.status == null)
          : statusFilter === 'rejected'
            ? rejectedRows
            : statusFilter === 'blocked' || statusFilter === 'deletion' || statusFilter === 'hidden'
              ? sellerRows.filter((row) => matchesSellerOperationalFilter(row, statusFilter))
              : mergedList;
  const isLoading =
    statusFilter == null ||
    isSellersLoading ||
    ((statusFilter === 'all' || statusFilter === 'pending') && isApplicationsLoading) ||
    (statusFilter === 'rejected' && isRejectedLoading);

  const tableData = useMemo(
    (): SellersTableRow[] =>
      list.map((row) => ({
        ...row,
        id: `${row.kind}-${row.id}`,
      })),
    [list],
  );

  const columns = useMemo<OptionsTableColumn<SellersTableRow>[]>(
    () => [
      {
        key: 'sellerOrAppId',
        label: 'ID',
        render: (row) =>
          row.kind === 'seller' ? row.sellerId : `Заявка #${row.applicationId}`,
      },
      {
        key: 'shopName',
        label: 'Магазин',
        render: (row) => row.shopName ?? '—',
      },
      {
        key: 'accountType',
        label: 'Тип',
        render: (row) => accountTypeLabel(row.accountType),
      },
      {
        key: 'status',
        label: 'Статус заявки',
        render: (row) =>
          row.kind === 'seller' ? (
            <span className="sellers-page__status-cell">
              <SellerAdminStatusBadge
                compact
                status={row.status}
                accountBlock={row.accountBlock}
                profileHidden={row.profileHidden}
                profileDeletion={row.profileDeletion}
              />
              {sellerIdsWithProfileChanges.has(row.sellerId) ? <SellerProfileChangesTag /> : null}
            </span>
          ) : (
            <SellerAdminStatusBadge status={row.status} />
          ),
      },
      {
        key: 'inn',
        label: 'ИНН',
        render: (row) => (row.inn != null ? String(row.inn) : '—'),
      },
      {
        key: 'phone',
        label: 'Телефон (аккаунт)',
        render: (row) => (row.phone ? formatPhoneNumber(row.phone) || '—' : '—'),
      },
      {
        key: 'products',
        label: 'Товаров',
        render: (row) => row.products ?? '—',
      },
      {
        key: 'pickupPoints',
        label: 'Пунктов выдачи',
        render: (row) => row.pickupPoints ?? '—',
      },
    ],
    [sellerIdsWithProfileChanges],
  );

  return (
    <div className="page-container sellers-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Продавцы' },
        ]}
      />

      <div className="page-header">
        <h1>Продавцы</h1>
      </div>

      <div className="sellers-page__toolbar">
        <div className="sellers-page__search">
          <Input
            className="sellers-page__search-input"
            type="text"
            placeholder="Поиск по названию, ИНН или номеру..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftAddon={<IoSearchOutline size={16} aria-hidden />}
          />
        </div>
        <QuietSelect<SellerStatusFilter>
          className="sellers-page__select-wrap"
          ariaLabel="Статус заявки"
          label={
            SELLER_STATUS_OPTIONS.find((option) => option.id === statusFilter)?.label ?? 'Все'
          }
          value={statusFilter ?? 'all'}
          onChange={setStatusFilter}
          options={SELLER_STATUS_OPTIONS.map((option) => ({
            value: option.id,
            label: option.label,
          }))}
        />
      </div>

      <div className="content-wrapper sellers-page__table-wrap">
        <OptionsTable<SellersTableRow>
          columns={columns}
          data={tableData}
          loading={isLoading}
          emptyMessage={searchQuery.trim() ? 'Ничего не найдено' : 'Нет продавцов'}
          onRowClick={(row) =>
            row.kind === 'seller'
              ? navigate(`/sellers/${row.sellerId}`)
              : navigate(`/sellers/applications/${row.applicationId}`)
          }
        />
      </div>
    </div>
  );
}
